import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField
} from "@material-ui/core";
import { CollapsiblePanel } from "src/apps/commons/dataDisplays";
import {
  listAllPartnerProducts,
  listAllPartners,
  listAllRemyndBrands,
  listAllRemyndCatLv1s,
  listAllRemyndCatLv2s,
  listAllRemyndCatLv3s
} from "src/apps/commons/dropdowns/api";
import AsyncDropdown from "src/apps/commons/dropdowns/components/AsyncDropdown";
import DateTimeTextField from "src/apps/commons/inputs/components/DateTimeTextField";
import InputThreeStateRadio from "src/apps/commons/inputs/components/InputThreeStateRadio";
import { IPartnerBrief } from "src/apps/modules/partner/types";
import { IPartnerProduct } from "src/apps/modules/partnerProduct/types";
import { IRemyndBrandBrief } from "src/apps/modules/remyndBrand/types";
import {
  IRemyndCategoryLv1Brief,
  IRemyndCategoryLv2Brief,
  IRemyndCategoryLv3Brief
} from "src/apps/modules/remyndCategory/types";
import { parseToOffsetDateTime } from "src/utils/dateUtils";
import { replaceEmptyString } from "src/utils";
import { RemyndProductFilterTypes } from "../types";

type FormData = {
  partnerDropdown?: IDropdown<IPartnerBrief>;
  partnerProductDropdown?: IDropdown<IPartnerProduct>;
  brandDropdown?: IDropdown<IRemyndBrandBrief>;
  categoryLv1Dropdown?: IDropdown<IRemyndCategoryLv1Brief>;
  categoryLv2Dropdown?: IDropdown<IRemyndCategoryLv2Brief>;
  categoryLv3Dropdown?: IDropdown<IRemyndCategoryLv3Brief>;
  noPartner?: boolean;
  noPartnerProduct?: boolean;
  noBrand?: boolean;
  noCategoryLv1?: boolean;
  noCategoryLv2?: boolean;
  noCategoryLv3?: boolean;
  categoryTextTh?: string;
  categoryTextEn?: string;
  noCategoryTextTh?: boolean;
  noCategoryTextEn?: boolean;
  name?: string;
  code?: string;
  refProductCode?: string;
  barcode?: string;
  hasImage?: boolean;
  highlighted?: boolean;
  searchable?: boolean;
  canceled?: boolean;
  active?: boolean;
  createdAtMin?: OffsetDateTime;
  createdAtMax?: OffsetDateTime;
  updatedAtMin?: OffsetDateTime;
  updatedAtMax?: OffsetDateTime;
};

interface Props {
  onSubmit: (filter: RemyndProductFilterTypes) => void;
}

const ListFilterPanel: React.FC<Props> = props => {
  const { onSubmit } = props;

  const defaultValues = useMemo<PartialNull<FormData>>(
    () => ({
      partnerDropdown: null,
      partnerProductDropdown: null,
      brandDropdown: null,
      categoryLv1Dropdown: null,
      categoryLv2Dropdown: null,
      categoryLv3Dropdown: null,
      name: "",
      code: "",
      refProductCode: "",
      barcode: "",
      categoryTextTh: "",
      categoryTextEn: ""
    }),
    []
  );
  const { handleSubmit, control, reset, watch } = useForm<
    PartialNull<FormData>
  >({ defaultValues: defaultValues });

  const handleTransformAndSubmit = (form: FormData) => {
    const {
      partnerDropdown: partner,
      partnerProductDropdown: partnerProduct,
      brandDropdown: brand,
      categoryLv1Dropdown: categoryLv1,
      categoryLv2Dropdown: categoryLv2,
      categoryLv3Dropdown: categoryLv3,
      createdAtMin,
      createdAtMax,
      updatedAtMin,
      updatedAtMax,
      ...restForm
    } = form;
    onSubmit({
      ...restForm,
      partnerIds: partner ? [partner.value] : undefined,
      partnerProductIds: partnerProduct ? [partnerProduct.value] : undefined,
      brandIds: brand ? [brand.value] : undefined,
      categoryLv1Ids: categoryLv1 ? [categoryLv1.value] : undefined,
      categoryLv2Ids: categoryLv2 ? [categoryLv2.value] : undefined,
      categoryLv3Ids: categoryLv3 ? [categoryLv3.value] : undefined,
      noPartner: restForm.noPartner || undefined,
      noPartnerProduct: restForm.noPartnerProduct || undefined,
      noBrand: restForm.noBrand || undefined,
      noCategoryLv1: restForm.noCategoryLv1 || undefined,
      noCategoryLv2: restForm.noCategoryLv2 || undefined,
      noCategoryLv3: restForm.noCategoryLv3 || undefined,
      noCategoryTextTh: restForm.noCategoryTextTh || undefined,
      noCategoryTextEn: restForm.noCategoryTextEn || undefined,
      name: replaceEmptyString(restForm.name),
      code: replaceEmptyString(restForm.code),
      refProductCode: replaceEmptyString(restForm.refProductCode),
      barcode: replaceEmptyString(restForm.barcode),
      categoryTextTh: replaceEmptyString(restForm.categoryTextTh),
      categoryTextEn: replaceEmptyString(restForm.categoryTextEn),
      createdAtMin: createdAtMin
        ? parseToOffsetDateTime(createdAtMin)
        : undefined,
      createdAtMax: createdAtMax
        ? parseToOffsetDateTime(createdAtMax, { sec: 59, ms: 999 })
        : undefined,
      updatedAtMin: updatedAtMin
        ? parseToOffsetDateTime(updatedAtMin)
        : undefined,
      updatedAtMax: updatedAtMax
        ? parseToOffsetDateTime(updatedAtMax, { sec: 59, ms: 999 })
        : undefined
    });
  };

  const categoryLv1Dropdown = watch("categoryLv1Dropdown");
  const categoryLv2Dropdown = watch("categoryLv2Dropdown");
  const partnerDropdown = watch("partnerDropdown");

  return (
    <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
      <CollapsiblePanel title="Search Filters" icon="filter" defaultCollapse>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Controller
              name="code"
              control={control}
              defaultValue={defaultValues.code}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  {...field}
                  label="รหัส"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="name"
              control={control}
              defaultValue={defaultValues.name}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  {...field}
                  label="ชื่อ (TH/EN)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="refProductCode"
              control={control}
              defaultValue={defaultValues.refProductCode}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  {...field}
                  label="Ref Product Code"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="barcode"
              control={control}
              defaultValue={defaultValues.barcode}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  {...field}
                  label="Barcode"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} />
          <Grid item xs={12} md={6}>
            <Controller
              name="brandDropdown"
              control={control}
              defaultValue={defaultValues.brandDropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  label="แบรนด์"
                  size="small"
                  fetchDropdown={listAllRemyndBrands}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box ml={1}>
              <FormControlLabel
                label="ไม่มีแบรนด์"
                control={
                  <Controller
                    name="noBrand"
                    control={control}
                    defaultValue={defaultValues.noBrand}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        color="primary"
                        checked={Boolean(value)}
                        onChange={onChange}
                      />
                    )}
                  />
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} />

          <Grid item xs={12} md={6}>
            <Controller
              name="categoryLv1Dropdown"
              control={control}
              defaultValue={defaultValues.categoryLv1Dropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  label="หมวดสินค้า Level 1"
                  size="small"
                  fetchDropdown={listAllRemyndCatLv1s}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box ml={1}>
              <FormControlLabel
                label="ไม่มีหมวดสินค้า Level 1"
                control={
                  <Controller
                    name="noCategoryLv1"
                    control={control}
                    defaultValue={defaultValues.noCategoryLv1}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        color="primary"
                        checked={Boolean(value)}
                        onChange={onChange}
                      />
                    )}
                  />
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="categoryLv2Dropdown"
              control={control}
              defaultValue={defaultValues.categoryLv2Dropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  label="หมวดสินค้า Level 2"
                  size="small"
                  fetchDropdown={listAllRemyndCatLv2s}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  additionalReqParams={{
                    search: { lv1Id: categoryLv1Dropdown?.value }
                  }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box ml={1}>
              <FormControlLabel
                label="ไม่มีหมวดสินค้า Level 2"
                control={
                  <Controller
                    name="noCategoryLv2"
                    control={control}
                    defaultValue={defaultValues.noCategoryLv2}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        color="primary"
                        checked={Boolean(value)}
                        onChange={onChange}
                      />
                    )}
                  />
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="categoryLv3Dropdown"
              control={control}
              defaultValue={defaultValues.categoryLv3Dropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  label="หมวดสินค้า Level 3"
                  size="small"
                  fetchDropdown={listAllRemyndCatLv3s}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  additionalReqParams={{
                    search: { lv2Id: categoryLv2Dropdown?.value }
                  }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box ml={1}>
              <FormControlLabel
                label="ไม่มีหมวดสินค้า Level 3"
                control={
                  <Controller
                    name="noCategoryLv3"
                    control={control}
                    defaultValue={defaultValues.noCategoryLv3}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        color="primary"
                        checked={Boolean(value)}
                        onChange={onChange}
                      />
                    )}
                  />
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} />

          <Grid item xs={12} md={6}>
            <Controller
              name="categoryTextTh"
              control={control}
              defaultValue={defaultValues.categoryTextTh}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  {...field}
                  label="หมวดสินค้า (text) (TH)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box ml={1}>
              <FormControlLabel
                label="ไม่มีหมวดสินค้า (text) (TH)"
                control={
                  <Controller
                    name="noCategoryTextTh"
                    control={control}
                    defaultValue={defaultValues.noCategoryTextTh}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        color="primary"
                        checked={Boolean(value)}
                        onChange={onChange}
                      />
                    )}
                  />
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="categoryTextEn"
              control={control}
              defaultValue={defaultValues.categoryTextEn}
              render={({ field }) => (
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  {...field}
                  label="หมวดสินค้า (text) (EN)"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box ml={1}>
              <FormControlLabel
                label="ไม่มีหมวดสินค้า (text) (EN)"
                control={
                  <Controller
                    name="noCategoryTextEn"
                    control={control}
                    defaultValue={defaultValues.noCategoryTextEn}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        color="primary"
                        checked={Boolean(value)}
                        onChange={onChange}
                      />
                    )}
                  />
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} />

          <Grid item xs={12} md={6}>
            <Controller
              name="partnerDropdown"
              control={control}
              defaultValue={defaultValues.partnerDropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  label="Partner"
                  size="small"
                  fetchDropdown={listAllPartners}
                  paginate={{
                    page: 1,
                    pageSize: 2000,
                    order: {
                      optionKey: "NAME",
                      desc: false
                    }
                  }}
                  additionalReqParams={{ excludeRemynd: true }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box ml={1}>
              <FormControlLabel
                label="ไม่มี Partner"
                control={
                  <Controller
                    name="noPartner"
                    control={control}
                    defaultValue={defaultValues.noPartner}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        color="primary"
                        checked={Boolean(value)}
                        onChange={onChange}
                      />
                    )}
                  />
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="partnerProductDropdown"
              control={control}
              defaultValue={defaultValues.partnerProductDropdown}
              render={({ field: { onChange, value } }) => (
                <AsyncDropdown
                  label="Partner Product"
                  size="small"
                  fetchDropdown={listAllPartnerProducts}
                  paginate={{
                    page: 1,
                    pageSize: 100,
                    order: {
                      optionKey: "NAME_TH",
                      desc: false
                    }
                  }}
                  additionalReqParams={{
                    partnerId: partnerDropdown?.value
                  }}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box ml={1}>
              <FormControlLabel
                label="ไม่มี Partner Product"
                control={
                  <Controller
                    name="noPartnerProduct"
                    control={control}
                    defaultValue={defaultValues.noPartnerProduct}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        color="primary"
                        checked={Boolean(value)}
                        onChange={onChange}
                      />
                    )}
                  />
                }
              />
            </Box>
          </Grid>

          <Grid item xs={12} />

          <Grid item xs={6} md={3}>
            <Controller
              name="createdAtMin"
              control={control}
              defaultValue={defaultValues?.createdAtMin ?? ""}
              render={({ field }) => (
                <DateTimeTextField
                  label="สร้าง ตั้งแต่"
                  size="small"
                  fullWidth
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="createdAtMax"
              control={control}
              defaultValue={defaultValues?.createdAtMax ?? ""}
              render={({ field }) => (
                <DateTimeTextField
                  label="สร้าง ไม่เกิน"
                  size="small"
                  fullWidth
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="updatedAtMin"
              control={control}
              defaultValue={defaultValues?.updatedAtMin ?? ""}
              render={({ field }) => (
                <DateTimeTextField
                  label="แก้ไขล่าสุด ตั้งแต่"
                  size="small"
                  fullWidth
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name="updatedAtMax"
              control={control}
              defaultValue={defaultValues?.updatedAtMax ?? ""}
              render={({ field }) => (
                <DateTimeTextField
                  label="แก้ไขล่าสุด ไม่เกิน"
                  size="small"
                  fullWidth
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} />

          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="hasImage"
                control={control}
                defaultValue={defaultValues.hasImage}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="มีรูปภาพ"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="searchable"
                control={control}
                defaultValue={defaultValues.searchable}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="Searchable (แสดงใน app)"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="highlighted"
                control={control}
                defaultValue={defaultValues.highlighted}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="Highlighted"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="active"
                control={control}
                defaultValue={defaultValues.active}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="Active"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box ml={1} mr={3}>
              <Controller
                name="canceled"
                control={control}
                defaultValue={defaultValues.canceled}
                render={({ field: { onChange, value } }) => (
                  <InputThreeStateRadio
                    label="Canceled"
                    size="small"
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            </Box>
          </Grid>

          <Grid item xs={12} />

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ marginRight: 8 }}
            >
              Apply Filters
            </Button>
            <Button
              variant="contained"
              color="default"
              type="button"
              onClick={() => {
                reset();
                handleTransformAndSubmit({});
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </CollapsiblePanel>
    </form>
  );
};

export default ListFilterPanel;
